import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MessageSquare,
  Search,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  HelpCircle,
  Sparkles,
  User as UserIcon,
  Bot,
  Calendar,
  Users as UsersIcon,
  Hash,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TYPE_META = {
  help: {
    label: 'Help Assistant',
    Icon: HelpCircle,
    pill: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  chat: {
    label: 'Pulse AI',
    Icon: Sparkles,
    pill: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  },
  legacy: {
    label: 'Legacy',
    Icon: MessageSquare,
    pill: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-200',
  },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, tone = 'primary' }) => {
  const tones = {
    primary: 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  };
  return (
    <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-4 flex items-center gap-4">
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold text-secondary-900 dark:text-white leading-tight">
          {value}
        </div>
        <div className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">{label}</div>
      </div>
    </div>
  );
};

const TypePill = ({ type }) => {
  const meta = TYPE_META[type] || TYPE_META.legacy;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.pill}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

const AdminChatHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    user_id: '',
    search: '',
    date_from: '',
    date_to: '',
  });
  const [searchInput, setSearchInput] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const searchDebounce = useRef(null);

  const totalPages = useMemo(
    () => (total ? Math.max(1, Math.ceil(total / pageSize)) : 1),
    [total, pageSize]
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/chat/admin/conversations/stats');
      setStats(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, page_size: pageSize });
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const res = await axios.get(`/api/chat/admin/conversations?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get('/api/team');
      setTeamMembers(res.data || []);
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchStats();
      fetchTeam();
    }
  }, [user, fetchStats, fetchTeam]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchInput.trim() }));
      setPage(1);
    }, 350);
    return () => searchDebounce.current && clearTimeout(searchDebounce.current);
  }, [searchInput]);

  const openConversation = async (id) => {
    setSelectedId(id);
    setSelectedConv(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(`/api/chat/admin/conversations/${id}`);
      setSelectedConv(res.data);
    } catch (e) {
      toast.error('Failed to load conversation');
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedId(null);
    setSelectedConv(null);
  };

  const resetFilters = () => {
    setFilters({ type: '', user_id: '', search: '', date_from: '', date_to: '' });
    setSearchInput('');
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (user && user.role !== 'admin') {
    return (
      <div className="card p-8 text-center">
        <h2 className="text-lg font-medium text-secondary-900 dark:text-white">Admin access required</h2>
        <p className="text-sm text-secondary-500 mt-1">You don't have permission to view chat history.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate('/profile');
            }}
            className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 text-secondary-600 dark:text-secondary-300"
            title="Back"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">Chat History</h1>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
              Every conversation users have had with the AI assistants.
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            fetchList();
            fetchStats();
          }}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={MessageSquare} tone="primary" label="Total conversations" value={stats.total_conversations} />
          <StatCard icon={Hash} tone="blue" label="Total messages" value={stats.total_messages} />
          <StatCard icon={UsersIcon} tone="purple" label="Unique users" value={stats.unique_users} />
          <StatCard icon={Clock} tone="green" label="Last 7 days" value={stats.conversations_last_7_days} />
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              Search title or message
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="e.g. leave balance, timesheet..."
                className="input pl-9 w-full"
              />
            </div>
          </div>

          <div className="w-full lg:w-48">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              Chat type
            </label>
            <select
              value={filters.type}
              onChange={(e) => {
                setFilters((f) => ({ ...f, type: e.target.value }));
                setPage(1);
              }}
              className="input w-full"
            >
              <option value="">All types</option>
              <option value="help">Help Assistant</option>
              <option value="chat">Pulse AI</option>
            </select>
          </div>

          <div className="w-full lg:w-56">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              User
            </label>
            <select
              value={filters.user_id}
              onChange={(e) => {
                setFilters((f) => ({ ...f, user_id: e.target.value }));
                setPage(1);
              }}
              className="input w-full"
            >
              <option value="">All users</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name || m.email}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full lg:w-40">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              From
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => {
                setFilters((f) => ({ ...f, date_from: e.target.value }));
                setPage(1);
              }}
              className="input w-full"
            />
          </div>

          <div className="w-full lg:w-40">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              To
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => {
                setFilters((f) => ({ ...f, date_to: e.target.value }));
                setPage(1);
              }}
              className="input w-full"
            />
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={resetFilters}
              className="btn-secondary flex items-center gap-2 whitespace-nowrap"
              title="Clear filters"
            >
              <X className="h-4 w-4" />
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <MessageSquare className="h-10 w-10 text-secondary-300 dark:text-secondary-600 mx-auto mb-3" />
            <p className="text-secondary-600 dark:text-secondary-300">No conversations found</p>
            <p className="text-xs text-secondary-500 mt-1">Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
              <thead className="bg-secondary-50 dark:bg-secondary-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Title / Preview</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Msgs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Last activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                {items.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => openConversation(row.id)}
                    className="hover:bg-secondary-50 dark:hover:bg-secondary-800/60 cursor-pointer transition"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-300 text-xs font-semibold">
                          {(row.user_name || '?').slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-secondary-900 dark:text-white truncate max-w-[180px]">
                            {row.user_name}
                          </div>
                          {row.user_email && (
                            <div className="text-xs text-secondary-500 truncate max-w-[180px]">{row.user_email}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <TypePill type={row.type} />
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <div className="text-sm font-medium text-secondary-900 dark:text-white truncate">{row.title}</div>
                      {row.last_message_preview && (
                        <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate mt-0.5">
                          <span className="font-medium uppercase tracking-wide mr-1">
                            {row.last_message_role === 'assistant' ? 'AI' : row.last_message_role || ''}:
                          </span>
                          {row.last_message_preview}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-700 dark:text-secondary-300">{row.message_count}</td>
                    <td className="px-4 py-3 text-sm text-secondary-700 dark:text-secondary-300 whitespace-nowrap">
                      {formatDate(row.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && items.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-200 dark:border-secondary-700">
            <div className="text-sm text-secondary-600 dark:text-secondary-400">
              Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span>
              –<span className="font-medium">{Math.min(page * pageSize, total)}</span> of{' '}
              <span className="font-medium">{total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-secondary px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeModal}>
          <div
            className="bg-white dark:bg-secondary-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b border-secondary-200 dark:border-secondary-700 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {selectedConv && <TypePill type={selectedConv.type || 'legacy'} />}
                  {selectedConv?.user && (
                    <span className="text-xs text-secondary-500">
                      • {selectedConv.user.full_name || selectedConv.user.email || 'Unknown user'}
                    </span>
                  )}
                </div>
                <h2 className="text-lg font-semibold text-secondary-900 dark:text-white truncate">
                  {selectedConv?.title || 'Conversation'}
                </h2>
                {selectedConv && (
                  <div className="mt-1 flex items-center gap-3 text-xs text-secondary-500">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(selectedConv.created_at)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      {selectedConv.messages?.length || 0} messages
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-secondary-50/40 dark:bg-secondary-950/40">
              {detailLoading ? (
                <div className="py-16 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : !selectedConv?.messages?.length ? (
                <div className="py-10 text-center text-sm text-secondary-500">No messages in this conversation.</div>
              ) : (
                selectedConv.messages.map((m, idx) => {
                  const isUser = m.role === 'user';
                  return (
                    <div key={idx} className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isUser
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                        }`}
                      >
                        {isUser ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </div>
                      <div
                        className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${
                          isUser
                            ? 'bg-primary-600 text-white'
                            : 'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white border border-secondary-200 dark:border-secondary-700'
                        }`}
                      >
                        <div
                          className={`prose prose-sm max-w-none ${
                            isUser
                              ? 'prose-invert [&_p]:text-white [&_*]:text-white'
                              : 'dark:prose-invert'
                          }`}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ''}</ReactMarkdown>
                        </div>
                        <div className={`mt-1 text-[10px] ${isUser ? 'text-primary-100' : 'text-secondary-400'}`}>
                          {formatDate(m.timestamp)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminChatHistory;
